import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, EyeOff, GripVertical } from 'lucide-react';
import { usePrivacy } from '../../hooks/usePrivacy.jsx';
import { openSideWindow, closeSideWindow, isSideWindowOpen } from '../../utils/sideWindow.js';
import { useAuth } from '../../hooks/useAuth.js';

/* The way out, on the screen itself.
 *
 * Whoever is sharing has a call in front of them and cannot go hunting through Settings for
 * this, so it sits on top of the page the way the Teams sharing bar does — and it can be
 * dragged, because wherever it is parked it is covering something. Its state is the same one
 * the preference in Preferences writes, so moving it here is remembered next time.
 *
 * It cannot arrive at the moment sharing starts: a page is never told that Teams began
 * sharing, because that happens outside the browser. What it can do is stay away when there is
 * nothing to guard — no hidden field on the screen, no control — which keeps it off the
 * dashboards and the project lists and leaves it only where it is doing something.
 *
 * Quiet when safe, loud when not. Hiding is the ordinary state, so it sits faded, shrunk to a
 * badge, and gives its name only to a pointer that has settled on it — a mouse crossing the
 * screen used to flash the whole explanation to everyone on the call. Showing is the state
 * somebody could walk away from and regret, so it stays open, in a warning colour.
 *
 * It is off unless asked for, because it is drawn on the page and so goes out with the share
 * like everything else; there is no drawing something the person can see and the room cannot.
 * The shortcut does the same job and costs no pixels. */

const COMPACT_W = 40;
const OPEN_W = 176;
const HEIGHT = 40;
const MARGIN = 8;

const posKeyFor = (userId) => `3ppms-privacy-pos:${userId ?? 'anon'}`;

/* Held inside the window by the size it is at rest, not the size it reaches when pointed at.
 * Clamping against the open width kept the badge a whole label's distance from the right-hand
 * edge and made the corner unreachable — which is the one place people want to park it, because
 * it is the one place covering nothing. Growing the other way handles the rest. */
function clamp(pos) {
    return {
        x: Math.min(Math.max(pos.x, MARGIN), Math.max(MARGIN, window.innerWidth - COMPACT_W - MARGIN)),
        y: Math.min(Math.max(pos.y, MARGIN), Math.max(MARGIN, window.innerHeight - HEIGHT - MARGIN)),
    };
}

function defaultPos() {
    return clamp({ x: window.innerWidth - COMPACT_W - MARGIN, y: window.innerHeight - HEIGHT - 80 });
}

/* Is there anything on this screen that this control governs? Attributes are watched as well
 * as added nodes, because the safety net in autoPrivate marks fields just after they arrive. */
function usePageHasSecrets() {
    const [has, setHas] = useState(false);

    useEffect(() => {
        let frame = 0;
        const check = () => {
            frame = 0;
            setHas(Boolean(document.querySelector('[data-private]')));
        };
        // React can fire a burst of mutations per render; one look per frame is plenty.
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(check);
        };

        check();
        const observer = new MutationObserver(schedule);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-private'],
        });

        return () => {
            observer.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    return has;
}

export default function PrivacyShield() {
    const { hidden, badgeVisible, toggle } = usePrivacy();
    const { user } = useAuth();
    const userId = user?.id;
    const hasSecrets = usePageHasSecrets();

    const [pos, setPos] = useState(defaultPos);
    const [hovered, setHovered] = useState(false);
    const [sideOpen, setSideOpen] = useState(false);
    const [sideBlocked, setSideBlocked] = useState(false);

    /* The window outlives this render and keeps calling back into it, so the callbacks read
     * through refs rather than closing over whatever was true when it opened. */
    const hiddenRef = useRef(hidden);
    const toggleRef = useRef(toggle);
    hiddenRef.current = hidden;
    toggleRef.current = toggle;
    const drag = useRef(null);
    const hoverTimer = useRef(0);

    /* A pointer on its way somewhere else is not a request to explain anything. Waiting for it
     * to settle is what keeps the panel off the shared screen. */
    const onEnter = useCallback(() => {
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setHovered(true), 550);
    }, []);

    const onLeave = useCallback(() => {
        clearTimeout(hoverTimer.current);
        setHovered(false);
    }, []);

    useEffect(() => () => clearTimeout(hoverTimer.current), []);

    /* The window can be shut from its own title bar, which says nothing back to us, and the
     * badge's own visibility now hangs on the answer. */
    useEffect(() => {
        const t = setInterval(() => setSideOpen(isSideWindowOpen()), 400);
        return () => clearInterval(t);
    }, []);

    const openSide = useCallback(async () => {
        if (isSideWindowOpen()) {
            closeSideWindow();
            setSideOpen(false);
            return;
        }
        // Started inside the click, or the browser refuses the window.
        const res = await openSideWindow({
            getHidden: () => hiddenRef.current,
            onToggle: () => toggleRef.current(),
        });
        setSideOpen(res.ok);
        setSideBlocked(!res.ok);
    }, [])

    useEffect(() => {
        try {
            const stored = localStorage.getItem(posKeyFor(userId));
            setPos(stored ? clamp(JSON.parse(stored)) : defaultPos());
        } catch {
            setPos(defaultPos());
        }
    }, [userId]);

    // A window that shrinks must not take the control off the edge with it.
    useEffect(() => {
        const onResize = () => setPos((p) => clamp(p));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const onPointerDown = useCallback((e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { startX: e.clientX, startY: e.clientY, dx: e.clientX - pos.x, dy: e.clientY - pos.y, moved: false };
    }, [pos]);

    const onPointerMove = useCallback((e) => {
        const d = drag.current;
        if (!d) return;
        // A few pixels of wobble during a click is not a drag, or the button would be
        // impossible to press with a trackpad.
        if (!d.moved && Math.abs(e.clientX - d.startX) < 4 && Math.abs(e.clientY - d.startY) < 4) return;
        d.moved = true;
        setPos(clamp({ x: e.clientX - d.dx, y: e.clientY - d.dy }));
    }, []);

    const onPointerUp = useCallback((e) => {
        const d = drag.current;
        drag.current = null;
        if (!d) return;
        e.currentTarget.releasePointerCapture(e.pointerId);

        if (!d.moved) {
            openSide();
            return;
        }
        setPos((p) => {
            try { localStorage.setItem(posKeyFor(userId), JSON.stringify(p)); } catch { /* ignore */ }
            return p;
        });
    }, [openSide, userId]);

    // With the control in the window, the badge has nothing left to do and every reason to go.
    if (!hasSecrets || sideOpen) return null;
    if (hidden && !badgeVisible) return null;

    const open = hovered || !hidden;
    // Parked near the top there is no room above it, so the explanation drops below instead.
    const below = pos.y < 130;
    /* Parked near the right there is no room to that side either, so it opens the other way.
     * The badge is what the pointer is on, so the badge is what has to stay put: anchoring the
     * pill by its right edge and reversing it keeps the icon exactly where it was and lets the
     * label run out to the left. Growing the whole thing leftwards instead would slide it out
     * from under the cursor, which reads as a flicker and loses the click. */
    const growsLeft = pos.x + OPEN_W > window.innerWidth - MARGIN;

    return (
        <div
            // A fixed anchor the size of the badge, so both the pill and its panel have a stable
            // edge to hang off whichever way they open.
            style={{ left: pos.x, top: pos.y, width: COMPACT_W, height: HEIGHT, zIndex: 9999 }}
            className="fixed print:hidden"
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            {/* Only ever on a settled pointer. The pill's colour already says which way things
                stand, so leaving the wording up as well just puts more of this on everybody
                else's screen. */}
            {hovered && (
                <div
                    role="tooltip"
                    className={`absolute w-64 rounded-lg bg-gray-900 text-white text-xs leading-relaxed px-3 py-2 shadow-xl ${below ? 'top-full mt-2' : 'bottom-full mb-2'
                        } ${growsLeft ? 'right-0' : 'left-0'}`}
                >
                    {/* Clicking the badge opens the window; there is nothing else for this panel
                        to offer, and every extra thing in it is another thing on the shared
                        screen while somebody reads it. */}
                    {hidden
                        ? <>Your address, phone, ID and bank details are blurred, so they will not show if you share your screen. Click to open them in a window your viewers cannot see.</>
                        : <>Your details are readable to anyone watching your screen. Click to open the private window, where you can blur them again.</>}
                    {sideBlocked && (
                        <span className="block mt-1 text-amber-300">Your browser blocked the window. Allow pop-ups for ProTrack and try again.</span>
                    )}
                    <span className="block mt-1 text-gray-400">Drag it to move it out of the way.</span>
                </div>
            )}

            <div
                role="button"
                aria-label="Open your details in a window your viewers cannot see" 
                tabIndex={0}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSide(); } }}
                style={{ width: open ? OPEN_W : COMPACT_W, height: HEIGHT }}
                className={`absolute top-0 flex items-center rounded-full shadow-lg border select-none touch-none cursor-pointer overflow-hidden transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${growsLeft ? 'right-0 flex-row-reverse' : 'left-0'
                    } ${hidden
                        ? 'bg-teal-500 border-teal-600 text-white focus:ring-teal-500'
                        : 'bg-amber-500 border-amber-600 text-white focus:ring-amber-500'
                    }`}
            >
                <span className="w-10 h-10 shrink-0 flex items-center justify-center">
                    {hidden ? <EyeOff size={17} /> : <Eye size={17} />}
                </span>
                <span className={`flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap transition-opacity duration-150 ${growsLeft ? 'pl-3' : 'pr-3'} ${open ? 'opacity-100' : 'opacity-0'}`}>
                    {hidden ? 'Details hidden' : 'Details visible'}
                    <GripVertical size={14} className="opacity-60" />
                </span>
            </div>
        </div>
    );
}
